// @flow

import React from "react";

import style from "./style.module.scss";
import { GatsbyImage } from "gatsby-plugin-image";
import { Row, Col } from "reactstrap";
import { t } from "ttag";
import { type RelatedAgency } from "./AgencyMapper";
import { createAgencySlug } from "./AgencyMapper";
import {
    Map,
    Pin,
    Pins,
    AgencyCard,
    AgencyContactBlock,
    RelatedAgencyPin,
} from "@2po-c21/components";
import AgencyMapper from "./AgencyMapper";

type Props = {
    agency: {
        id: string,
        slug: string,
        name: string,
        phoneNumber: string,
        images: Array<{
            description: string,
            fixed: *,
        }>,
        email: string,
        location: { longitude: string, latitude: string },
        address: {
            city: string,
            postalCode: string,
            number: string,
            street: string,
            countryCode: string,
        },
        relatedAgencies: Array<RelatedAgency>,
        api_id?: string,
    },
    fallbackImage: string,
    locale?: string,
    title?: string,
    isAgencyPage?: boolean,
};

const AgencyInformation = ({
    agency: { location },
    agency,
    fallbackImage,
    locale,
    title,
    isAgencyPage,
}: Props) => {
    if (!location || !location.latitude || !location.longitude) {
        return null;
    }
    const { longitude, latitude } = location;

    const image = agency.images[0] || fallbackImage;

    // Ensure relatedAgencies is an empty array if null
    const safeRelatedAgencies = agency?.relatedAgencies || [];

    const cardImage = {
        // eslint-disable-next-line react/display-name
        imageSrc: () => (
            <GatsbyImage
                image={image?.gatsbyImageData}
                alt={image?.description}
                backgroundColor
            />
        ),
        description: image?.description,
    };

    const getContactProps = {
        label: t`property-details.property-description.estate-agent`,
        agencyName: agency?.name,
        image: {
            // eslint-disable-next-line react/display-name
            imageSrc: () => {
                if (!image?.gatsbyImageData) {
                    return null;
                }

                // TODO: fix this inline style -- it's a hack to make the image take the full width of the container
                return (
                    <GatsbyImage
                        image={image?.gatsbyImageData}
                        alt={image?.description}
                        backgroundColor
                        style={{
                            width: "100%",
                            height: "100%",
                        }}
                    />
                );
            },
            description: image?.description,
        },
        visitPropertyBtnText: t`property-details.property-description.visit-property`,
        phoneNumber: agency?.phoneNumber,
        address: {
            street: agency?.address?.street,
            number: agency?.address?.number,
            postalCode: agency?.address?.postalCode,
            city: agency?.address?.city,
            country: agency?.address?.countryCode,
        },
    };

    const scrollToElement = (elementId) => {
        const element = document.querySelector(elementId);
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
        }
    };

    const handleVisitProperty = () => {
        //On form succes & estimation form page -  go to the agency page form
        if (!isAgencyPage) {
            const agencyUrl =
                createAgencySlug(
                    agency.name,
                    agency.api_id || agency.id,
                    locale,
                ) + "#agency-contact-form";

            window.location.href = agencyUrl;
        } else {
            // Default behavior - scroll to contact form on current page
            scrollToElement("#agency-contact-form");
        }
    };

    return (
        <div className={style.agencyInformation} id="agency-information">
            <div className={style.title}>{title ?? t`agency.map.title`}</div>
            <Row className={style.agencyInformationRow}>
                <Col lg="4" className={style.contactColumn}>
                    {agency?.relatedAgencies != null &&
                    agency?.relatedAgencies.length > 0 ? (
                        <AgencyMapper
                            agency={agency}
                            relatedAgencies={agency?.relatedAgencies}
                            relatedAgenciesTitle={t`agency.map.related-agencies.title`}
                            fallbackImage={fallbackImage}
                            locale={locale}
                        />
                    ) : (
                        <AgencyContactBlock
                            wideCard={false}
                            mapCard={true}
                            {...getContactProps}
                            onClickVisitProperty={handleVisitProperty}
                            LinkToCallAgency={(children) => {
                                if (!agency.phoneNumber) {
                                    return undefined;
                                }
                                return (
                                    <a
                                        className={style.agencyPhoneNumber}
                                        href={`tel:${agency.phoneNumber}`}
                                    >
                                        {children}
                                    </a>
                                );
                            }}
                            contactAgencyBtnText={t`property-details.property-description.contact-agency`}
                        />
                    )}
                </Col>
                <Col lg="8" className={style.mapColumn}>
                    <div className={style.mapDesktop}>
                        <Map>
                            <Pins>
                                <Pin
                                    latitude={latitude}
                                    longitude={longitude}
                                    icon={RelatedAgencyPin}
                                    iconVariant={"relentless-gold"}
                                >
                                    <AgencyCard
                                        agency={agency}
                                        image={cardImage}
                                        phoneNumber={agency.phoneNumber}
                                    />
                                </Pin>
                                {safeRelatedAgencies &&
                                    safeRelatedAgencies
                                        .filter(
                                            (relatedAgency) =>
                                                relatedAgency?.location
                                                    ?.latitude &&
                                                relatedAgency?.location
                                                    ?.longitude,
                                        )
                                        .map((relatedAgency) => (
                                            <Pin
                                                key={relatedAgency.id}
                                                latitude={
                                                    relatedAgency.location
                                                        .latitude
                                                }
                                                longitude={
                                                    relatedAgency.location
                                                        .longitude
                                                }
                                                icon={RelatedAgencyPin}
                                            />
                                        ))}
                            </Pins>
                        </Map>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default AgencyInformation;
